// Feedback > Low Score Alerts — who gets told when a customer scores the
// product badly, and what has gone out.
//
// This screen exists because of a specific mistake worth not repeating: a
// category list once lived as a constant in a Netlify function, and Rachael was
// assigned a task to "correct the categories the scanner uses" for a list
// nothing had ever shown her. Carman has said no to this alert for now and MJ
// has not answered — both of those are toggles here, so changing the answer is
// a click rather than a deploy.
//
// Everything rendered here is computed server-side in lib/feedback-alerts.js.
// The page derives no numbers of its own, so it cannot disagree with the email.
import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Bell, BellOff, Check, Mail, Plus, RefreshCw, Trash2, X } from 'lucide-react';
import { feedbackAlert } from '../services/api';

function fmtWhen(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return String(ts);
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

const STATUS_STYLE = {
  sent: { bg: '#e7f4ec', fg: '#1e7a3c', label: 'Sent' },
  seeded: { bg: '#eef1f4', fg: '#5a6672', label: 'Not mailed' },
  claimed: { bg: '#fdf1e3', fg: '#b26a00', label: 'In flight' },
};

export default function FeedbackAlerts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [preview, setPreview] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try { setData(await feedbackAlert('status')); }
    catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (label, fn) => {
    setBusy(label); setErr(''); setMsg('');
    try { await fn(); await load(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(''); }
  };

  const cfg = data?.config || {};
  const recipients = data?.recipients || [];
  const enabledCount = recipients.filter((r) => r.enabled).length;
  const last30 = data?.last30 || { low: 0, total: 0 };

  return (
    <div>
      <div className="breadcrumb">
        <a href="#feedback">Feedback</a>
        <span> / </span>
        <span>Low Score Alerts</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: 0, color: 'var(--navy)' }}>Low Score Alerts</h1>
          <p style={{ margin: '6px 0 0', color: '#5a6672', maxWidth: 720 }}>
            Sends one email the moment a customer rates the <strong>product</strong> at
            {' '}{cfg.threshold || 2} or lower. Everything else, including every good response,
            stays in the Monday feedback summary and is never mailed from here.
          </p>
        </div>
        <button className="btn btn-outline" onClick={load} disabled={loading}>
          <RefreshCw size={14} style={{ marginRight: 6 }} />Refresh
        </button>
      </div>

      {err && (
        <div className="card" style={{ borderLeft: '4px solid #b3261e', marginBottom: 16 }}>
          <div className="card-body" style={{ color: '#b3261e' }}>
            <AlertTriangle size={14} style={{ marginRight: 6, verticalAlign: -2 }} />{err}
          </div>
        </div>
      )}
      {msg && (
        <div className="card" style={{ borderLeft: '4px solid var(--orange)', marginBottom: 16 }}>
          <div className="card-body">{msg}</div>
        </div>
      )}

      {/* The volume answer. Carman's objection was never the idea, it was that
          she would be buried — so the screen leads with the measured number. */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em', color: '#5a6672' }}>
              Would send, last 30 days
            </div>
            <div style={{ fontSize: 30, fontWeight: 700, color: 'var(--navy)' }}>{last30.low}</div>
            <div style={{ fontSize: 12, color: '#5a6672' }}>
              about {(last30.low / 30).toFixed(1)} a day
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em', color: '#5a6672' }}>
              All responses, same period
            </div>
            <div style={{ fontSize: 30, fontWeight: 700, color: '#5a6672' }}>{last30.total}</div>
            <div style={{ fontSize: 12, color: '#5a6672' }}>
              what the full feed would have sent
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em', color: '#5a6672' }}>
              Waiting to go out
            </div>
            <div style={{ fontSize: 30, fontWeight: 700, color: 'var(--navy)' }}>{data?.queued ?? '-'}</div>
            <div style={{ fontSize: 12, color: '#5a6672' }}>picked up within about 15 minutes</div>
          </div>
        </div>
      </div>

      {/* ── settings ── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><span>Settings</span></div>
        <div className="card-body" style={{ display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Alert when the product score is at or below</label>
            <select
              className="form-select" style={{ maxWidth: 120 }}
              value={cfg.threshold || 2}
              disabled={!!busy}
              onChange={(e) => act('threshold', () => feedbackAlert('config', { threshold: Number(e.target.value) }))}
            >
              {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n} or lower</option>)}
            </select>
          </div>
          <div>
            <button
              className={cfg.enabled ? 'btn btn-outline' : 'btn btn-primary'}
              disabled={!!busy}
              onClick={() => act('enabled', () => feedbackAlert('config', { enabled: !cfg.enabled }))}
            >
              {cfg.enabled
                ? <><BellOff size={14} style={{ marginRight: 6 }} />Pause alerts</>
                : <><Bell size={14} style={{ marginRight: 6 }} />Turn alerts on</>}
            </button>
            <div style={{ fontSize: 12, color: '#5a6672', marginTop: 6 }}>
              {cfg.enabled ? 'Alerts are running.' : 'Paused. Responses queue up and are not lost.'}
            </div>
          </div>
          <div>
            <button
              className="btn btn-outline" disabled={!!busy}
              onClick={() => act('preview', async () => {
                const p = await feedbackAlert('preview', { limit: 3 });
                setPreview(p);
                if (!p.alerts.length) setMsg('Nothing is waiting, so there is nothing to preview right now.');
              })}
            >
              <Mail size={14} style={{ marginRight: 6 }} />Preview what would send
            </button>
          </div>
        </div>
      </div>

      {/* ── recipients ── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <span>Who gets these ({enabledCount} {enabledCount === 1 ? 'person' : 'people'})</span>
          <button className="btn btn-ghost" onClick={() => setAdding((v) => !v)} disabled={!!busy}>
            {adding ? <X size={14} /> : <><Plus size={14} style={{ marginRight: 4 }} />Add someone</>}
          </button>
        </div>
        <div className="card-body">
          {adding && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
              <input
                className="form-input" style={{ maxWidth: 260 }} placeholder="name@crabplace.com"
                value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
              />
              <input
                className="form-input" style={{ maxWidth: 200 }} placeholder="Name (optional)"
                value={newName} onChange={(e) => setNewName(e.target.value)}
              />
              <button
                className="btn btn-primary" disabled={!newEmail.trim() || !!busy}
                onClick={() => act('add', async () => {
                  await feedbackAlert('add-recipient', { email: newEmail.trim(), name: newName.trim() || null });
                  setNewEmail(''); setNewName(''); setAdding(false);
                })}
              >Add</button>
            </div>
          )}

          {!recipients.length && !loading && (
            <div style={{ color: '#5a6672' }}>
              Nobody is on the list. Alerts will queue up rather than send.
            </div>
          )}

          <table className="product-table" style={{ width: '100%' }}>
            <tbody>
              {recipients.map((r) => (
                <tr key={r.email} style={{ opacity: r.enabled ? 1 : 0.55 }}>
                  <td style={{ width: 44 }}>
                    <span
                      title={r.enabled ? 'Receiving alerts' : 'Not receiving alerts'}
                      style={{
                        display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
                        background: r.enabled ? '#1e7a3c' : '#c2c9d0',
                      }}
                    />
                  </td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{r.name || r.email}</div>
                    {r.name && <div style={{ fontSize: 12, color: '#5a6672' }}>{r.email}</div>}
                  </td>
                  <td style={{ fontSize: 12, color: '#5a6672', maxWidth: 380 }}>{r.note || ''}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button
                      className="btn btn-ghost" disabled={!!busy}
                      onClick={() => act('toggle', () => feedbackAlert('set-recipient', { email: r.email, enabled: !r.enabled }))}
                    >
                      {r.enabled ? 'Turn off' : <><Check size={14} style={{ marginRight: 4 }} />Turn on</>}
                    </button>
                    <button
                      className="btn btn-ghost" disabled={!!busy} title="Remove"
                      onClick={() => act('remove', () => feedbackAlert('remove-recipient', { email: r.email }))}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── preview ── */}
      {preview && !!preview.alerts?.length && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <span>Preview — nothing here has been sent</span>
            <button className="btn btn-ghost" onClick={() => setPreview(null)}><X size={14} /></button>
          </div>
          <div className="card-body">
            {preview.alerts.map((a) => (
              <div key={a.feedId} style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 12, color: '#5a6672', marginBottom: 6 }}>{a.subject}</div>
                <div dangerouslySetInnerHTML={{ __html: a.html }} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── history ── */}
      <div className="card">
        <div className="card-header"><span>Recent</span></div>
        <div className="card-body">
          {!data?.recent?.length && !loading && (
            <div style={{ color: '#5a6672' }}>Nothing yet.</div>
          )}
          {!!data?.recent?.length && (
            <table className="product-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Order</th><th>Product score</th><th>Status</th><th>When</th><th>Note</th>
                </tr>
              </thead>
              <tbody>
                {data.recent.map((r) => {
                  const st = STATUS_STYLE[r.status] || { bg: '#eef1f4', fg: '#5a6672', label: r.status };
                  return (
                    <tr key={r.feed_id}>
                      <td>{r.bc_order_id || r.feed_id}</td>
                      <td>{r.prod_rate == null ? '-' : `${r.prod_rate} / 5`}</td>
                      <td>
                        <span style={{
                          background: st.bg, color: st.fg, borderRadius: 10,
                          padding: '2px 9px', fontSize: 12, fontWeight: 600,
                        }}>{st.label}</span>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtWhen(r.sent_at || r.claimed_at)}</td>
                      <td style={{ fontSize: 12, color: '#5a6672' }}>{r.note || r.recipients || ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
